import { PIECES_IDENTITE, LIBELLE_PIECE } from '../../src/partage/pieces-identite.js';
import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi, type CompteAdmin, type InvitationAdmin, type InvitationEmise } from './api';

/**
 * COMPTES ET HABILITATIONS (§45).
 *
 * ⚠️ CET ÉCRAN COMBLE UN TROU MESURÉ. `sec.compte:creer` et
 * `sec.habilitation:creer` existaient dans la table des permissions depuis la
 * migration 008 — et aucune route, donc aucun écran, ne les consommait. Les
 * quatre comptes du registre avaient été écrits à la main en SQL. Une
 * permission déclarée que rien ne consomme est le symétrique du bouton sans
 * action : elle donne à lire une capacité qui n'existe pas.
 */

const ROLES = [
  'agent_cadastre', 'geometre', 'notaire', 'agent_fiscal', 'agent_urbanisme',
  'agent_collectivite',
  'agent_immobilier', 'promoteur', 'auditeur', 'administrateur', 'titulaire', 'citoyen',
] as const;

/** Ce que chaque rôle ouvre — pour qu'on n'accorde pas au hasard. */
const CE_QUE_LE_ROLE_OUVRE: Record<string, string> = {
  agent_cadastre: 'parcelles, mutations, levés, documents — et le NOMINATIF (sensibilité 3)',
  geometre: 'dépôt et contrôle de levés ; ne valide pas les siens',
  notaire: 'titres et actes, données nécessaires à une mutation',
  agent_fiscal: 'rapprochement fiscal, sans l’identité des titulaires',
  agent_urbanisme: 'servitudes et restrictions',
  agent_immobilier: 'biens et transactions',
  promoteur: 'consultation limitée',
  auditeur: 'journal et comptes, en lecture',
  administrateur: 'comptes et habilitations — PAS le nominatif',
  titulaire: 'ses propres biens',
  citoyen: 'vérification publique',
};

export function Comptes({ peutAdministrer }: { peutAdministrer: boolean }) {
  const [comptes, setComptes] = useState<CompteAdmin[]>([]);
  const [invitations, setInvitations] = useState<InvitationAdmin[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [emise, setEmise] = useState<InvitationEmise | null>(null);
  const [inactifs, setInactifs] = useState(false);
  const [formulaire, setFormulaire] = useState(false);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const [c, i] = await Promise.all([
        api.comptes.liste(inactifs),
        api.comptes.invitations().catch(() => ({ invitations: [] })),
      ]);
      setComptes(c.comptes ?? []);
      setInvitations(i.invitations ?? []);
    } catch (e) {
      setErreur(e instanceof ErreurApi && e.estRefus
        ? "Votre rôle ne permet pas d'administrer les comptes (sec.compte:lire)."
        : String((e as Error).message ?? e));
    }
  }, [inactifs]);

  useEffect(() => { void charger(); }, [charger]);

  async function agir(action: () => Promise<{ message: string }>) {
    setErreur(null); setMessage(null);
    try { setMessage((await action()).message); await charger(); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : String((e as Error).message ?? e)); }
  }

  return (
    <div className="tableau-bord">
      <header className="entete-scene">
        <h2>Comptes et habilitations</h2>
        <div className="actions">
          <label className="case">
            <input type="checkbox" checked={inactifs} onChange={(e) => setInactifs(e.target.checked)} />
            Voir les comptes fermés
          </label>
          {peutAdministrer && (
            <button className="bouton primaire" onClick={() => { setEmise(null); setFormulaire(true); }}>
              Inviter un agent
            </button>
          )}
        </div>
      </header>

      <div className="avertissement">
        <strong>Un compte ne se crée pas en nommant quelqu'un.</strong> L'identifiant que
        Mowibo ID livrera est un numéro interne que ni vous ni l'agent ne connaissez avant
        sa première connexion. Vous décrivez donc <em>qui</em> et <em>avec quels rôles</em> ;
        c'est sa connexion qui apporte l'identifiant.
      </div>

      {message && <p className="succes" role="status">{message}</p>}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {emise && <CodeRemis emise={emise} onFerme={() => setEmise(null)} />}
      {formulaire && (
        <FormulaireInvitation
          onFerme={() => setFormulaire(false)}
          onEmise={(i) => { setFormulaire(false); setEmise(i); void charger(); }}
        />
      )}

      <h3 className="titre-section">Comptes ({comptes.length})</h3>
      {comptes.length === 0 ? <p className="vide">Aucun compte.</p> : (
        <table className="tableau large">
          <thead><tr>
            <th>Nom</th><th>Rôles</th><th>Portée</th><th>Sessions</th><th>État</th><th /></tr>
          </thead>
          <tbody>
            {comptes.map((c) => (
              <tr key={c.id}>
                <td>
                  <strong>{c.nom_affichage ?? '—'}</strong>
                  {c.organisation && <><br /><span className="aide">{c.organisation}</span></>}
                </td>
                <td>
                  {c.roles.length === 0 ? <span className="aide">aucun — citoyen par défaut</span> : (
                    <div className="jetons">
                      {c.roles.map((r) => (
                        <span key={r} className="fanion" title={CE_QUE_LE_ROLE_OUVRE[r] ?? r}>
                          {r}
                          {peutAdministrer && (
                            <button
                              className="lien retrait" title={`Retirer ${r}`}
                              onClick={() => {
                                const motif = window.prompt(`Retirer « ${r} » à ${c.nom_affichage} — motif :`);
                                if (motif && motif.trim().length >= 3) {
                                  void agir(() => api.comptes.retirerRole(c.id, r, motif.trim()));
                                }
                              }}
                            >×</button>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td>{c.portee_nationale
                  ? 'nationale'
                  : (c.ressort_division ?? 'limitée')}</td>
                <td className="num">{c.sessions_vivantes}</td>
                <td><span className={`etat ${c.actif ? 'vert' : 'rouge'}`}>{c.actif ? 'actif' : 'fermé'}</span></td>
                <td className="actions-ligne">
                  {peutAdministrer && (
                    <>
                      <button className="bouton discret" onClick={() => {
                        const role = window.prompt(`Rôle à accorder à ${c.nom_affichage} :\n${ROLES.join(', ')}`);
                        if (!role) return;
                        /**
                         * ⚠️ CE BOUTON ACCORDAIT LE PAYS ENTIER SANS LE DIRE : il
                         * appelait habiliter() sans portée, et le serveur lisait
                         * l'absence comme « nationale ». On demande désormais, et
                         * on PROPOSE le ressort de l'organisation quand il est
                         * connu — sans jamais le présumer quand il ne l'est pas.
                         */
                        const ressort = c.ressort_division_id;
                        const choix = window.prompt(
                          ressort
                            ? `Portée pour ${c.nom_affichage} :\n`
                              + `  1 — ${c.ressort_division} (ressort de ${c.organisation})\n`
                              + `  2 — nationale (le pays entier)`
                            : `Portée pour ${c.nom_affichage} :\n`
                              + `  2 — nationale (le pays entier)\n\n`
                              + `Son organisation${c.organisation ? ` (${c.organisation})` : ''} n'a pas de ressort `
                              + `résolu dans le référentiel : aucune portée limitée n'est proposable ici.`,
                          ressort ? '1' : '2');
                        if (choix !== '1' && choix !== '2') return;
                        if (choix === '1' && !ressort) return;
                        const motif = window.prompt('Motif — consigné au journal (10 caractères au moins) :');
                        if (motif && motif.trim().length >= 10) {
                          void agir(() => api.comptes.habiliter(c.id, {
                            role: role.trim(), motif: motif.trim(),
                            portee: choix === '1' ? 'division' : 'nationale',
                            porteeDivisionId: choix === '1' ? ressort! : undefined,
                          }));
                        }
                      }}>Habiliter</button>
                      <button className="bouton discret danger" onClick={() => {
                        const motif = window.prompt(
                          `${c.actif ? 'Fermer' : 'Rouvrir'} le compte de ${c.nom_affichage} — motif :`);
                        if (motif && motif.trim().length >= 3) {
                          void agir(() => api.comptes.activite(c.id, !c.actif, motif.trim()));
                        }
                      }}>{c.actif ? 'Fermer' : 'Rouvrir'}</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 className="titre-section">Invitations ({invitations.filter((i) => i.utilisable).length} utilisable(s))</h3>
      {invitations.length === 0 ? <p className="vide">Aucune invitation émise.</p> : (
        <table className="tableau large">
          <thead><tr><th>Destinataire</th><th>Rôles</th><th>Émise par</th><th>Échéance</th><th>État</th></tr></thead>
          <tbody>
            {invitations.map((i) => (
              <tr key={i.id}>
                <td><strong>{i.nom_affichage}</strong><br /><span className="aide">{i.motif}</span></td>
                <td>{i.roles.join(', ')}</td>
                <td>{i.creee_par ?? '—'}</td>
                <td>{new Date(i.expire_le).toLocaleString('fr-FR')}</td>
                <td>
                  {i.consommee_le
                    ? <span className="etat gris">utilisée</span>
                    : i.utilisable
                      ? <span className="etat vert">en attente</span>
                      : <span className="etat rouge">expirée</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * ⚠️ LE CODE N'EST AFFICHÉ QU'ICI, ET UNE SEULE FOIS. La base n'en garde que
 * l'empreinte : ni un administrateur, ni l'exploitant de la base ne peuvent le
 * relire. Le dire à l'écran est nécessaire — sans cela, on ferme la fenêtre en
 * pensant le retrouver dans la liste.
 */
function CodeRemis({ emise, onFerme }: { emise: InvitationEmise; onFerme: () => void }) {
  const [copie, setCopie] = useState(false);
  return (
    <div className="fiche-formulaire">
      <h3>Code d'invitation — affiché une seule fois</h3>
      <pre className="brut code-invitation">{emise.code}</pre>
      <p>{emise.consigne}</p>
      <p className="mention">
        Échéance : {new Date(emise.expireLe).toLocaleString('fr-FR')}.
        Un code perdu ne se retrouve pas — il faut en émettre un autre.
      </p>
      <div className="actions">
        <button className="bouton discret" onClick={() => {
          void navigator.clipboard?.writeText(emise.code).then(() => setCopie(true)).catch(() => setCopie(false));
        }}>{copie ? 'Copié' : 'Copier'}</button>
        <button className="bouton primaire" onClick={onFerme}>J'ai noté le code</button>
      </div>
    </div>
  );
}

function FormulaireInvitation(
  { onFerme, onEmise }: { onFerme: () => void; onEmise: (i: InvitationEmise) => void },
) {
  const [nom, setNom] = useState('');
  const [roles, setRoles] = useState<string[]>(['agent_cadastre']);
  const [jours, setJours] = useState(7);
  const [motif, setMotif] = useState('');
  /**
   * ⚠️ LA PORTÉE N'AVAIT AUCUN CHAMP DANS CE FORMULAIRE, et c'est LÀ que le
   * défaut naissait. Le serveur traitait l'absence comme « nationale » : toute
   * invitation émise depuis cet écran créait un agent habilité sur les 988 695
   * parcelles du pays. Mesuré le 18/09/2026 : 16 habilitations, 16 nationales.
   * Aucun formulaire ne doit pouvoir accorder par omission ce qu'il ne sait pas
   * exprimer — ni valeur par défaut ici, ni repli côté serveur.
   */
  const [portee, setPortee] = useState<'nationale' | 'division' | ''>('');
  const [divisionId, setDivisionId] = useState('');
  const [divisions, setDivisions] = useState<{ id: string; nom: string; niveau: number }[]>([]);

  useEffect(() => {
    if (portee !== 'division' || divisions.length > 0) return;
    void api.territoire.divisions()
      .then((r) => setDivisions((r.divisions as unknown as { id: string; nom: string; niveau: number }[]) ?? []))
      .catch(() => setDivisions([]));
  }, [portee, divisions.length]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  // Identite civile, declaree par l'administration qui nomme.
  const [nomCivil, setNomCivil] = useState('');
  const [prenoms, setPrenoms] = useState('');
  const [pieceType, setPieceType] = useState('');
  const [pieceNumero, setPieceNumero] = useState('');
  const [pieceAutorite, setPieceAutorite] = useState('');

  // La nomination.
  const [fonction, setFonction] = useState('');
  const [matricule, setMatricule] = useState('');
  const [telephone, setTelephone] = useState('');
  const [courriel, setCourriel] = useState('');
  const [acte, setActe] = useState('');
  const [nommeLe, setNommeLe] = useState('');

  /**
   * ⚠️ CE QUI REND L'HABILITATION OPPOSABLE SE DIT AVANT D'ÉMETTRE, pas le jour
   * où un acte est contesté : une personne identifiée, nommée par un acte.
   * Le reste de la fiche est du confort administratif — on ne bloque pas
   * dessus, on le montre.
   */
  const opposable = nomCivil.trim().length >= 2 && acte.trim().length >= 3;

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null); setEnvoi(true);
    try {
      // ⚠️ ON N'ENVOIE QUE CE QUI EST RENSEIGNÉ. Une chaîne vide n'est pas une
      // absence : envoyée, elle échoue la validation de longueur minimale du
      // serveur, et le formulaire refuse sans dire pourquoi.
      const siRempli = (v: string) => { const t = v.trim(); return t.length > 0 ? t : undefined; };
      onEmise(await api.comptes.inviter({
        nomAffichage: nom.trim(), roles, validiteJours: jours, motif: motif.trim(),
        portee: portee as 'nationale' | 'division',
        porteeDivisionId: portee === 'division' ? divisionId : undefined,
        nom: siRempli(nomCivil), prenoms: siRempli(prenoms),
        pieceType: (siRempli(pieceType) as never) ?? undefined,
        pieceNumero: siRempli(pieceNumero), pieceAutorite: siRempli(pieceAutorite),
        fonction: siRempli(fonction), matricule: siRempli(matricule),
        telephoneService: siRempli(telephone), courrielService: siRempli(courriel),
        acteNomination: siRempli(acte), nommeLe: siRempli(nommeLe),
      }));
    } catch (err) {
      setErreur(err instanceof ErreurApi ? err.message : String(err));
    } finally { setEnvoi(false); }
  }

  return (
    <form className="fiche-formulaire" onSubmit={soumettre}>
      <h3>Enrôler un agent</h3>
      <p className="aide">
        L'administration nomme&nbsp;; l'agent ne se déclare pas lui-même. Ce qui est
        saisi ici part à trois endroits distincts&nbsp;: la <strong>personne</strong> au
        registre des titulaires, la <strong>pièce</strong> au registre des identifiants,
        la <strong>nomination</strong> sur le compte.
      </p>

      <fieldset className="cadre">
        <legend><span className="cadre-n">1</span> Identité de l'agent</legend>
        <p className="cadre-aide">
          Telle que l'acte de nomination la porte. Si cette personne est déjà connue du
          registre par son Mowibo ID, elle sera rapprochée — jamais dupliquée.
        </p>
        <div className="cadre-corps">
          <label>NOM <span className="aide">en capitales, forme administrative</span>
          <input value={nomCivil} onChange={(e) => setNomCivil(e.target.value.toUpperCase())}
                 placeholder="DIALLO" minLength={2} maxLength={100} />
        </label>
        <label>Prénoms
          <input value={prenoms} onChange={(e) => setPrenoms(e.target.value)}
                 placeholder="Mariama Bobo" maxLength={150} />
        </label>
        <label>Nom affiché <span className="aide">ce que verront les autres agents</span>
          <input value={nom} onChange={(e) => setNom(e.target.value)}
                 placeholder="DIALLO Mariama — agent cadastre" required minLength={2} />
        </label>
        </div>
      </fieldset>

      <fieldset className="cadre">
        <legend><span className="cadre-n">2</span> Pièce d'identité</legend>
        <p className="cadre-aide">
          Type et numéro vont ensemble, ou ne viennent pas du tout&nbsp;: un numéro sans
          type ne se vérifie pas, un type sans numéro ne désigne rien.
        </p>
        <div className="cadre-corps">
          <label>Type
          <select value={pieceType} onChange={(e) => setPieceType(e.target.value)}>
            <option value="">— non déclarée —</option>
            {/* ⚠️ LA LISTE VIENT DU PARTAGE, ELLE N'EST PAS RECOPIÉE ICI.
                Mon premier jet proposait « Carte nationale d'identité » — qui
                n'existe pas dans la contrainte de la base — et omettait la
                CARTE D'ÉLECTEUR, qui est la pièce courante en Guinée.
                L'enrôlement échouait après que le code ait été remis à l'agent. */}
            {PIECES_IDENTITE.map((p) => (
              <option key={p} value={p}>{LIBELLE_PIECE[p]}</option>
            ))}
          </select>
        </label>
        <label>Numéro
          <input value={pieceNumero} onChange={(e) => setPieceNumero(e.target.value)}
                 placeholder="AB1234567" maxLength={60} disabled={!pieceType} />
        </label>
        <label>Autorité qui l'a délivrée
          <input value={pieceAutorite} onChange={(e) => setPieceAutorite(e.target.value)}
                 placeholder="Direction Générale de la Police Nationale" maxLength={200}
                 disabled={!pieceType} />
        </label>
        </div>
      </fieldset>

      <fieldset className="cadre">
        <legend><span className="cadre-n">3</span> Nomination</legend>
        <p className="cadre-aide">
          La fonction et l'acte appartiennent à l'administration, pas à la personne&nbsp;:
          ils ne la suivent pas d'une commune à l'autre.
        </p>
        <div className="cadre-corps">
          <label>Fonction <span className="aide">l'intitulé du poste, pas le rôle technique</span>
          <input value={fonction} onChange={(e) => setFonction(e.target.value)}
                 placeholder="Chef du service foncier" maxLength={200} />
        </label>
        <label>Matricule <span className="aide">unique dans cette organisation</span>
          <input value={matricule} onChange={(e) => setMatricule(e.target.value)}
                 placeholder="MAT-2026-014" maxLength={60} />
        </label>
        <label>Acte de nomination <span className="aide">arrêté, décision, note de service</span>
          <input value={acte} onChange={(e) => setActe(e.target.value)}
                 placeholder="Arrêté communal n° 2026-047 du 12/09/2026" maxLength={300} />
        </label>
        <label>Prise de fonction
          <input type="date" value={nommeLe} onChange={(e) => setNommeLe(e.target.value)}
                 max={new Date().toISOString().slice(0, 10)} />
        </label>
        <label>Téléphone de service
          <input value={telephone} onChange={(e) => setTelephone(e.target.value)}
                 placeholder="+224 6XX XX XX XX" maxLength={40} />
        </label>
        <label>Courriel de service
          <input type="email" value={courriel} onChange={(e) => setCourriel(e.target.value)}
                 placeholder="agent@commune.gov.gn" maxLength={200} />
        </label>
        </div>
      </fieldset>

      <fieldset className="cadre">
        <legend><span className="cadre-n">4</span> Habilitation</legend>

      <div className="cadre-corps">
          <fieldset className="roles">
        <legend>Rôles accordés <span className="aide">figés à l'émission</span></legend>
        {ROLES.map((r) => (
          <label key={r} className="case" title={CE_QUE_LE_ROLE_OUVRE[r]}>
            <input
              type="checkbox" checked={roles.includes(r)}
              onChange={(e) => setRoles((v) => e.target.checked ? [...v, r] : v.filter((x) => x !== r))}
            />
            <span>{r}</span>
            <em className="aide">{CE_QUE_LE_ROLE_OUVRE[r]}</em>
          </label>
        ))}
      </fieldset>

      <fieldset className="roles">
        <legend>Portée territoriale <span className="aide">§45 — aucune valeur par défaut</span></legend>
        <label className="case">
          <input type="radio" name="portee" checked={portee === 'division'}
                 onChange={() => setPortee('division')} />
          <span>Une division</span>
          <em className="aide">l'agent ne voit que son ressort — et tout ce qu'il contient</em>
        </label>
        {portee === 'division' && (
          <label>Division
            <select value={divisionId} onChange={(e) => setDivisionId(e.target.value)} required>
              <option value="">— choisir —</option>
              {divisions.map((d) => (
                <option key={d.id} value={d.id}>{'· '.repeat(Math.max(0, d.niveau - 1))}{d.nom}</option>
              ))}
            </select>
          </label>
        )}
        <label className="case">
          <input type="radio" name="portee" checked={portee === 'nationale'}
                 onChange={() => setPortee('nationale')} />
          <span>Nationale</span>
          <em className="aide">le pays entier — réservé aux administrations nationales</em>
        </label>
      </fieldset>

      <label>Validité
        <select value={jours} onChange={(e) => setJours(Number(e.target.value))}>
          <option value={1}>1 jour</option><option value={7}>7 jours</option>
          <option value={14}>14 jours</option><option value={30}>30 jours</option>
        </select>
      </label>
      <label>Motif <span className="aide">consigné au journal, non modifiable ensuite</span>
        <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={2} minLength={3} required />
      </label>
        </div>
      </fieldset>

      {/*
        ⚠️ ON N'INTERDIT PAS D'ÉMETTRE SANS ACTE — une commune peut avoir à
        habiliter en urgence. On DIT ce que ça coûte : une habilitation dont on ne
        peut pas produire le fondement ne s'oppose à personne, et on le
        découvrirait le jour où un acte serait contesté.
      */}
      {!opposable && (
        <p className="avertissement" role="status">
          Sans <strong>nom</strong> ni <strong>acte de nomination</strong>, cette
          habilitation ne sera pas opposable&nbsp;: on saura qu'un agent signe, pas en
          vertu de quoi. L'écran des comptes la signalera comme incomplète.
        </p>
      )}

      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      <div className="actions">
        <button type="button" className="bouton discret" onClick={onFerme}>Annuler</button>
        <button className="bouton primaire"
                disabled={envoi || nom.trim().length < 2 || roles.length === 0 || motif.trim().length < 3
                          || portee === '' || (portee === 'division' && !divisionId)}>
          {envoi ? 'Émission…' : 'Émettre le code'}
        </button>
      </div>
    </form>
  );
}
